// ============================================
// AppShell — Main layout wrapper
// Mobile-aware: disables hover expand on touch devices
// ============================================

import { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import type { UserRole } from '../../types';

interface AppShellProps {
  role: UserRole;
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

function useIsMobile(breakpoint = 767) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

export default function AppShell({ role, children, activeTab, onTabChange }: AppShellProps) {
  const [collapsed, setCollapsed] = useState(true);
  const isMobile = useIsMobile();

  const handleTabChange = (tab: string) => {
    onTabChange(tab);
    setCollapsed(true);
  };

  // On mobile, sidebar is always collapsed (bottom tab bar)
  const effectiveCollapsed = isMobile ? true : collapsed;

  return (
    <div className="app-layout">
      <Sidebar
        role={role}
        collapsed={effectiveCollapsed}
        onToggle={() => !isMobile && setCollapsed(!collapsed)}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onMouseEnter={isMobile ? undefined : () => setCollapsed(false)}
        onMouseLeave={isMobile ? undefined : () => setCollapsed(true)}
      />
      <main className={`app-main ${effectiveCollapsed ? 'app-main--collapsed' : ''}`}>
        <Header />
        <div className="app-content app-content-transition" key={activeTab}>
          {children}
        </div>
      </main>
    </div>
  );
}
